"use client";

import type { Currency, DropTableItem } from "@loot-os/api-contract";

import { Badge } from "@loot-os/ui/components/badge";
import {
  Popover,
  PopoverContent,
  PopoverTitle,
  PopoverTrigger,
} from "@loot-os/ui/components/popover";
import {
  LIMITED_ITEM_BADGE,
  limitedItemDisclosure,
} from "@loot-os/ui/lib/marketplaceItemDisclosure";
import { InfoIcon } from "lucide-react";

/**
 * The marker on a drop-table entry whose supply is counted, opening the rules a shopper needs
 * before they spend anything: who else can take it, what happens when they cannot withdraw it,
 * and what an outside seller's item adds on top.
 *
 * The caller decides which entries get one (`isLimitedEntry`), so this renders whatever it is
 * given. The text is `limitedItemDisclosure`, which the inventory page reads too.
 */
export function LimitedItemBadge({
  item,
  currency,
}: {
  item: DropTableItem;
  /** The environment's display currency; the sell line names it. */
  currency: Pick<Currency, "namePlural">;
}) {
  return (
    <Popover>
      {/* The Badge is a <span>, so Base UI supplies the button role, focus and Enter/Space. */}
      <PopoverTrigger
        nativeButton={false}
        render={
          <Badge
            variant="outline"
            className="cursor-pointer gap-1"
            aria-label={`${LIMITED_ITEM_BADGE}: what this means`}
          />
        }
      >
        {LIMITED_ITEM_BADGE}
        <InfoIcon aria-hidden="true" />
      </PopoverTrigger>
      <PopoverContent align="start" className="gap-2">
        <PopoverTitle>{LIMITED_ITEM_BADGE}</PopoverTitle>
        {limitedItemDisclosure(item, currency).map((line) => (
          <p key={line} className="text-muted-foreground text-xs">
            {line}
          </p>
        ))}
      </PopoverContent>
    </Popover>
  );
}
